<script>
import moment from "moment";
import SvgIcon from "@/shared/ui/svg-icon/SvgIcon.vue";

export default {
  name: "AiSideBar",
  components: { SvgIcon },
  props: {
    sessions: {
      type: Array,
      default: () => [],
    },
    currentSession: {
      type: Object,
      default: null,
    },
  },
  emits: ["newChat", "update:currentSession", "delete-session", "clearHistory"],
  data() {
    return {
      searchTerm: "",
    };
  },
  computed: {
    filteredSessions() {
      if (!this.searchTerm) return this.sessions;
      return this.sessions.filter((session) =>
        session.title.toLowerCase().includes(this.searchTerm.toLowerCase())
      );
    },
    groupedSessions() {
      const today = moment().startOf("day");
      const yesterday = moment().subtract(1, "day").startOf("day");

      const groups = {
        today: [],
        yesterday: [],
        pastWeek: [],
      };

      this.filteredSessions.forEach((session) => {
        const sessionDate = moment(session.createdAt);
        if (sessionDate.isSame(today, "day")) {
          groups.today.push(session);
        } else if (sessionDate.isSame(yesterday, "day")) {
          groups.yesterday.push(session);
        } else {
          groups.pastWeek.push(session);
        }
      });

      return groups;
    },
  },
  methods: {
    newChat() {
      this.$emit("newChat");
    },
    setCurrentSession(session) {
      this.$emit("update:currentSession", session);
    },
    deleteSession(session) {
      this.$emit("delete-session", session);
    },
    async clearHistory() {
      try {
        await this.$confirm(
          this.$t("Вы действительно хотите удалить всю историю?"),
          this.$t("Внимание"),
          {
            confirmButtonText: this.$t("Да"),
            cancelButtonText: this.$t("Отмена"),
            type: "warning",
          }
        );
        this.$emit("clearHistory");
      } catch {
        // отмена
      }
    },
  },
};
</script>

<template>
  <div class="ai-sidebar">
    <div class="sidebar-menu">
      <div class="group-title">{{ $t("меню") }}</div>
      <div
        class="menu-item"
        :class="{
          active: currentSession && currentSession.messages.length === 0,
        }"
        @click="newChat"
      >
        <SvgIcon name="huge-plus" class="w-3 h-3" />
        <span>{{ $t("Новый чат") }}</span>
      </div>
    </div>

    <div class="sidebar-chats">
      <div class="group-title-row">
        <div class="group-title">{{ $t("чаты") }}</div>
        <div
          v-if="sessions.length > 0"
          class="clear-btn"
          :title="$t('Очистить историю')"
          @click="clearHistory"
        >
          <SvgIcon name="huge-danger" class="w-3.5 h-3.5" />
        </div>
      </div>

      <template v-if="groupedSessions.today.length">
        <div class="date-label">{{ $t("Сегодня") }}</div>
        <div
          v-for="session in groupedSessions.today"
          :key="session.id"
          class="chat-item"
          :class="{ active: session === currentSession }"
          @click="setCurrentSession(session)"
        >
          <div class="chat-title">{{ session.title }}</div>
          <div class="chat-delete" @click.stop="deleteSession(session)">
            <SvgIcon name="huge-close" class="w-3 h-3" />
          </div>
        </div>
      </template>

      <template v-if="groupedSessions.yesterday.length">
        <div class="date-label">{{ $t("Вчера") }}</div>
        <div
          v-for="session in groupedSessions.yesterday"
          :key="session.id"
          class="chat-item"
          :class="{ active: session === currentSession }"
          @click="setCurrentSession(session)"
        >
          <div class="chat-title">{{ session.title }}</div>
          <div class="chat-delete" @click.stop="deleteSession(session)">
            <SvgIcon name="huge-x" class="w-3 h-3" />
          </div>
        </div>
      </template>

      <template v-if="groupedSessions.pastWeek.length">
        <div class="date-label">{{ $t("За неделю") }}</div>
        <div
          v-for="session in groupedSessions.pastWeek"
          :key="session.id"
          class="chat-item"
          :class="{ active: session === currentSession }"
          @click="setCurrentSession(session)"
        >
          <div class="chat-title">{{ session.title }}</div>
          <div class="chat-delete" @click.stop="deleteSession(session)">
            <SvgIcon name="huge-x" class="w-3 h-3" />
          </div>
        </div>
      </template>

      <div
        v-if="
          !groupedSessions.today.length &&
          !groupedSessions.yesterday.length &&
          !groupedSessions.pastWeek.length
        "
        class="empty-placeholder"
      >
        {{ $t("История чатов пуста") }}
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.ai-sidebar {
  @apply bg-white border-r border-border;
  width: 280px;
  min-width: 250px;
  height: 100%;
  overflow-y: auto;
  padding: 12px;
  display: flex;
  flex-direction: column;
}

.sidebar-menu {
  margin-bottom: 16px;
}

.group-title {
  text-transform: uppercase;
  font-size: 10px;
  font-weight: 500;
  color: rgb(var(--color-gray-400));
  margin-bottom: 6px;
  letter-spacing: 0.05em;
}

.group-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.clear-btn {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  cursor: pointer;
  color: hsl(var(--muted-foreground));
  transition: background-color 0.15s ease, color 0.15s ease;

  &:hover {
    background-color: rgb(var(--color-red-500) / 0.12);
    color: rgb(var(--color-red-500));
  }
}

.menu-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 8px;
  height: 34px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  color: rgb(var(--color-gray-700));
  border: 1px solid transparent;

  &:hover {
    background-color: rgb(var(--color-white));
    border-color: rgb(var(--color-gray-200));
    color: rgb(var(--color-gray-900));
  }

  &.active {
    background-color: rgb(var(--color-white));
    border-color: rgb(var(--color-gray-200));
    color: rgb(var(--color-gray-900));
  }
}

.sidebar-chats {
  flex: 1;
}

.date-label {
  font-size: 11px;
  font-weight: 500;
  color: rgb(var(--color-gray-400));
  margin: 12px 0 4px 4px;
}

.chat-item {
  display: flex;
  align-items: center;
  padding: 0 8px;
  height: 34px;
  border-radius: 6px;
  cursor: pointer;
  border: 1px solid transparent;
  margin-bottom: 2px;

  .chat-title {
    flex: 1;
    font-size: 13px;
    color: rgb(var(--color-gray-600));
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    mask-image: linear-gradient(to right, black 80%, transparent 100%);
    -webkit-mask-image: linear-gradient(to right, black 80%, transparent 100%);
  }

  .chat-delete {
    flex-shrink: 0;
    width: 22px;
    height: 22px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    opacity: 0;
    color: rgb(var(--color-gray-400));
    transition: opacity 0.2s;

    &:hover {
      background-color: rgb(var(--color-red-100));
      color: rgb(var(--color-red-500));
    }
  }

  &:hover {
    background-color: rgb(var(--color-white));
    border-color: rgb(var(--color-gray-200));

    .chat-delete {
      opacity: 1;
    }
  }

  &.active {
    background-color: rgb(var(--color-white));
    border-color: rgb(var(--color-gray-200));

    .chat-title {
      color: rgb(var(--color-gray-900));
    }
  }
}

.empty-placeholder {
  font-size: 12px;
  color: rgb(var(--color-gray-400));
  padding: 8px 4px;
}
</style>
